import React, { useState, useCallback, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import { addDays, isWithinInterval, format, startOfMonth } from "date-fns";
import "react-day-picker/style.css";
import "./RangeDeSelectCalendar.css";

/** Parse a YYYY-MM-DD string to Date in LOCAL timezone safely */
const parseSafe = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d === "string" && d.match(/^\d{4}-\d{2}-\d{2}$/))
    return new Date(d + "T00:00:00");
  return new Date(d);
};

const formatLocalISO = (date) => {
  if (!date || isNaN(new Date(date).getTime())) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const RangeDeSelectCalendar = ({
  onConfirm,
  onClose,
  maxDays = null,
  initialRange,
  initialDeselected = new Set(),
  initialAdded = new Set(),
  activityData = [],        // array of { date: "YYYY-MM-DD", ... } records from DB
  dbExcludedDates = new Set(), // Set of "YYYY-MM-DD" strings where dayInclude === "no"
}) => {
  // Yesterday = latest selectable date
  // Calculate the latest date we have data for
  const latestDataDateKey = (() => {
    if (!activityData || activityData.length === 0) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - 1);
      return formatLocalISO(d);
    }
    // activityData is already sorted descending in some places, but let's be safe
    const sorted = [...activityData].sort((a, b) => b.date.localeCompare(a.date));
    return sorted[0].date;
  })();

  const latestSelectableDate = new Date(latestDataDateKey + "T00:00:00");
  const [mode, setMode] = useState(() =>
    initialRange?.from && initialRange?.to ? "exclude" : "range"
  );
  const [range, setRange] = useState(() => {
    const f = parseSafe(initialRange?.from);
    const t = parseSafe(initialRange?.to);
    if (!f && !t) return undefined;
    return { from: f, to: t || undefined };
  });


  const [excluded, setExcluded] = useState(new Set(initialDeselected));

  const [added, setAdded] = useState(new Set(initialAdded));
  const [autoExclude, setAutoExclude] = useState(false);
  // Store auto-computed keys in refs — no visual update, applied only at Confirm
  const autoExcludedRangeKeysRef = useRef(new Set()); // in-range dates to exclude at confirm
  const autoExcludedAddedKeysRef = useRef(new Set()); // extra-added dates to drop at confirm
  const skipNextEffectRef = useRef(false);

  const toKey = (d) => formatLocalISO(d);


  const expandRange = useCallback((from, to) => {
    if (!from || !to) return [];
    const dates = [];
    let cur = new Date(from);
    const end = new Date(to);
    while (cur <= end) {
      dates.push(new Date(cur));
      cur = addDays(cur, 1);
    }
    return dates;
  }, []);

  const allRangeDates = range?.from && range?.to ? expandRange(range.from, range.to) : [];
  const totalDays = allRangeDates.length;
  const includedCount = totalDays - excluded.size + added.size;

  const recordDateSet = new Set((activityData || []).map(r => r.date).filter(Boolean));


  const computeAutoTargets = useCallback((currentAdded) => {
    const rangeKeys = new Set();
    const addedKeys = new Set();


    allRangeDates.forEach(d => {
      const key = toKey(d);
      if (!recordDateSet.has(key) || dbExcludedDates.has(key)) {
        rangeKeys.add(key);
      }
    });

    (currentAdded || added).forEach(key => {
      if (!recordDateSet.has(key) || dbExcludedDates.has(key)) {
        addedKeys.add(key);
      }
    });

    return { rangeKeys, addedKeys };

  }, [allRangeDates, added, activityData, dbExcludedDates]);


  const handleAutoExcludeToggle = (checked) => {
    setAutoExclude(checked);
    skipNextEffectRef.current = true;

    if (checked) {
      // Compute which dates qualify — but do NOT touch excluded/added state
      const { rangeKeys, addedKeys } = computeAutoTargets(added);
      autoExcludedRangeKeysRef.current = rangeKeys;
      autoExcludedAddedKeysRef.current = addedKeys;
    } else {
      autoExcludedRangeKeysRef.current = new Set();
      autoExcludedAddedKeysRef.current = new Set();
    }
  };

  useEffect(() => {
    if (!autoExclude) return;
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }
    // Range changed while auto-exclude is on — recompute silently (no visual update)
    const { rangeKeys, addedKeys } = computeAutoTargets(added);
    autoExcludedRangeKeysRef.current = rangeKeys;
    autoExcludedAddedKeysRef.current = addedKeys;
  }, [range, autoExclude, activityData, dbExcludedDates]);

  const isInRange = (day) => {
    if (!range?.from || !range?.to) return false;
    return isWithinInterval(day, { start: range.from, end: range.to });
  };

  const handleDayClick = (day) => {
    // Block clicks on days after the latest data record
    if (toKey(day) > latestDataDateKey) return;

    const key = toKey(day);

    if (mode === "range") {

      if (!range?.from || (range?.from && range?.to)) {

        setRange({ from: day, to: undefined });
      } else {

        if (day < range.from) {
          setRange({ from: day, to: undefined });
        } else {
          setRange({ from: range.from, to: day });
          setExcluded(new Set());
          setAdded(new Set());
          setMode("exclude");
        }
      }
    } else {
      const inRange = isInRange(day);
      if (inRange) {
        setExcluded((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
      } else {
        setAdded((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
      }
    }
  };

  const clearRange = () => {
    setMode("range");
    setRange(undefined);
    setExcluded(new Set());
    setAdded(new Set());
    setAutoExclude(false);
    autoExcludedRangeKeysRef.current = new Set();
    autoExcludedAddedKeysRef.current = new Set();
    skipNextEffectRef.current = false;
  };

  const handleConfirm = () => {
    if (!onConfirm || !range?.from || !range?.to) return;

    // Merge manual excluded + auto-excluded range keys (applied silently, no visual change)
    const finalExcluded = new Set(excluded);
    if (autoExclude) {
      autoExcludedRangeKeysRef.current.forEach(k => finalExcluded.add(k));
    }

    // Filter out auto-excluded added keys from the final added list
    const finalAdded = autoExclude
      ? Array.from(added).filter(k => !autoExcludedAddedKeysRef.current.has(k))
      : Array.from(added);

    onConfirm({
      from: range.from,
      to: range.to,
      deselected: allRangeDates.filter((d) => finalExcluded.has(toKey(d))),
      added: finalAdded.map((s) => new Date(s + "T00:00:00")),
    });
  };

  const defaultMonth = range?.from ? startOfMonth(range.from) : startOfMonth(new Date());
  const disabledCap = [
    { after: latestSelectableDate },
    ...(maxDays && range?.from && !range?.to
      ? [{ after: addDays(range.from, maxDays - 1) }]
      : []),
  ];

  return (
    <div className="rdsc-wrapper">
      <div className="rdsc-header">
        <div className="rdsc-phases">
          {mode === "range" ? (
            <span className="rdsc-hint">
              {!range?.from
                ? "Pick a start day"
                : range?.from && !range?.to
                ? `From: ${format(range.from, "d MMM")} — now pick an end day`
                : "Select a new date range (or confirm current)"}
            </span>
          ) : (
            <>
              <span className="rdsc-badge-inline">
                {format(range.from, "d MMM")} → {format(range.to, "d MMM yyyy")}
              </span>
               <span className="rdsc-legend-item">
            <span  /> Active ({totalDays - excluded.size + added.size})
          </span>
            </>
          )}
        </div>
        <button className="rdsc-close-btn" onClick={onClose} title="Close" aria-label="Close calendar">
          ✕
        </button>
      </div>

      <div className={`rdsc-calendar rdsc-mode-${mode}`}>
        <DayPicker
          defaultMonth={defaultMonth}
          numberOfMonths={2}
          modifiers={{
            rangeStart: (day) => mode === "range" && range?.from && range?.to && toKey(day) === toKey(range.from),
            rangeEnd: (day) => mode === "range" && range?.to && toKey(day) === toKey(range.to),
            rangeMiddle: (day) => mode === "range" && range?.from && range?.to && day > range.from && day < range.to,
            singleSelected: (day) => mode === "range" && range?.from && !range?.to && toKey(day) === toKey(range.from),

            inRangeIncluded: (day) => isInRange(day) && !excluded.has(toKey(day)),
            inRangeDeselected: (day) => isInRange(day) && excluded.has(toKey(day)),
            extraAdded: (day) => !isInRange(day) && added.has(toKey(day)),
            outsideRange: (day) => !isInRange(day) && !added.has(toKey(day)),
            futureDay: (day) => toKey(day) > latestDataDateKey,
          }}
          modifiersClassNames={{
            rangeStart: "rdp-day_selected rdp-day_range_start",
            rangeEnd: "rdp-day_selected rdp-day_range_end",
            rangeMiddle: "rdp-day_selected rdp-day_range_middle",
            singleSelected: "rdsc-single-selected",

            inRangeIncluded: "rdsc-in-range-included",
            inRangeDeselected: "rdsc-in-range-deselected",
            extraAdded: "rdsc-extra-added",
            outsideRange: "rdsc-outside-range",
            futureDay: "rdsc-future-day",
          }}
          onDayClick={handleDayClick}
          disabled={disabledCap}
        />
      </div>

      {mode === "exclude" && (
        <div className="rdsc-legend">
       
          {activityData.length > 0 && (
            <label className="rdsc-auto-exclude-label" title="Automatically exclude dates with no recorded activity or already excluded in the database">
              <input
                type="checkbox"
                className="rdsc-auto-exclude-checkbox"
                checked={autoExclude}
                onChange={(e) => handleAutoExcludeToggle(e.target.checked)}
              />
              <span className="rdsc-auto-exclude-text">
                Auto Exclude
                <span className="rdsc-auto-exclude-hint">
                  (no record days / Marked excluded)
                </span>
              </span>
            </label>
          )}
        </div>
      )}

      {/* ── Actions ── */}
      <div className="rdsc-actions">
        <button className="rdsc-btn rdsc-btn-secondary" onClick={clearRange}>
          Reset Range
        </button>
        {range?.from && range?.to && (
          <button className="rdsc-btn rdsc-btn-primary" onClick={handleConfirm}>
            Confirm ({includedCount} days)
          </button>
        )}
      </div>
    </div>
  );
};

export default RangeDeSelectCalendar;
